import { useCallback, useState } from 'react';
import { Checkbox, Grid, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type { EnvironmentalDataFormData } from '../../../types/reports.js';
import { EMISSION_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { NO_REMOUNT, ValidatedNumberInput } from '../../form/validatedFields.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { KILN_ANNUAL_FIELDS } from './KilnForm.js';

interface KilnAnnualValuesFormProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  kilnIdx: number;
  disabled?: boolean;
}

// Liest einen verschachtelten Formularwert anhand eines Punktpfads -- dieselbe Logik wie das
// (nicht exportierte) getPath in validatedFields.tsx. Lokal dupliziert statt importiert, exakte
// Praezedenz: KilnForm.tsx Zeilen 105-115 und BelowLimitToggle.tsx duplizieren dieselben vier
// Zeilen aus demselben Grund -- getPath bleibt modulprivat in validatedFields.tsx.
function getNestedValue(values: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, values);
}

/**
 * Die neun Jahresmittelwerte am Ofen (D-01/D-11), dreispaltig wie die Messfelder in
 * `KilnForm.tsx`. HCl und HF (D-02) tragen zusaetzlich ein Haekchen fuer "diskontinuierlich
 * gemessen": ist es gesetzt, gibt es fuer diesen Ofen keinen Jahresmittelwert dieser Komponente
 * mehr -- die Werte stehen dann ausschliesslich bei den Einzelmessungen (D-12).
 *
 * Kein Bestimmungsgrenzen-Umschalter an diesen neun Feldern (D-07 gilt nur fuer die 26
 * diskontinuierlichen Werte an der Messung) -- ein Umschalter hier waere ein stiller
 * Umfangsgewinn.
 *
 * Sperr-/Leerlogik der beiden Haekchen (D-12, T-27.1-17): das Formular ist uncontrolled --
 * ein Formularwert-Setzen mit unterdruecktem Key-Bump loest kein Re-Render aus --, der
 * Haekchenzustand lebt deshalb je Komponente (HCl/HF) in eigenem lokalem State plus einem eigenen
 * Formular-Abonnement direkt im Render-Body (kein umschliessender zusaetzlicher Effekt-Hook:
 * Mantine registriert seinen eigenen Watcher-Effekt bereits selbst und meldet ihn im Cleanup
 * wieder ab, siehe `MeasurementTitle` in `KilnMeasurementCard.tsx`; ein weiterer Effekt-Hook
 * drumherum waere ein Hook-Aufruf in einem Callback und stuerzt zur Laufzeit mit "Invalid hook
 * call" ab).
 *
 * Beim Setzen des Haekchens wird der aktuelle Wert in einem sitzungsweiten lokalen
 * Zwischenspeicher abgelegt und der Formularwert auf `null` gesetzt -- OHNE `NO_REMOUNT`: der
 * Key-Bump ist hier ausdruecklich gewollt, nur er laesst das uncontrolled Zahlenfeld mit leerem
 * `defaultValue` neu aufsetzen, sodass es auch sichtbar leer wird. Beim Entfernen kehrt der
 * Zwischenspeicher in denselben Formularwert zurueck (ebenfalls mit Key-Bump) und wird selbst auf
 * `null` zurueckgesetzt. Der gespeicherte (und damit gesendete) Wert ist waehrend des gesetzten
 * Haekchens `null` -- das ist die Aussage aus D-02 ("kein Jahresmittelwert dieser Komponente"),
 * kein Datenverlust: ein Entfernen in derselben Sitzung stellt ihn wieder her, erst ein Neuladen
 * loescht ihn endgueltig, weil dann tatsaechlich keiner mehr vorliegt.
 */
export function KilnAnnualValuesForm({ form, kilnIdx, disabled }: KilnAnnualValuesFormProps) {
  const hclDiscontinuousPath = `rotaryKilns.${kilnIdx}.hclDiscontinuous`;
  const hfDiscontinuousPath = `rotaryKilns.${kilnIdx}.hfDiscontinuous`;

  const [hclChecked, setHclChecked] = useState<boolean>(() =>
    Boolean(getNestedValue(form.getValues(), hclDiscontinuousPath)),
  );
  const [hfChecked, setHfChecked] = useState<boolean>(() =>
    Boolean(getNestedValue(form.getValues(), hfDiscontinuousPath)),
  );
  // Zwischenspeicher fuer den geleerten Wert, siehe Klassenkommentar oben -- ueberdauert nur die
  // Sitzung, wird nie an das Formular oder den Server gesendet.
  const [hclStash, setHclStash] = useState<string | null>(null);
  const [hfStash, setHfStash] = useState<string | null>(null);

  const handleHclDiscontinuousChange = useCallback(({ value }: { value: unknown }) => {
    setHclChecked(Boolean(value));
  }, []);
  const handleHfDiscontinuousChange = useCallback(({ value }: { value: unknown }) => {
    setHfChecked(Boolean(value));
  }, []);

  form.watch(hclDiscontinuousPath, handleHclDiscontinuousChange);
  form.watch(hfDiscontinuousPath, handleHfDiscontinuousChange);

  function handleToggle(
    discontinuousPath: string,
    valuePath: string,
    stashedValue: string | null,
    setStashedValue: (value: string | null) => void,
    setChecked: (value: boolean) => void,
    next: boolean,
  ): void {
    if (next) {
      const current = getNestedValue(form.getValues(), valuePath) as string | null | undefined;
      setStashedValue(current ?? null);
      form.setFieldValue(valuePath, null);
    } else {
      form.setFieldValue(valuePath, stashedValue);
      setStashedValue(null);
    }
    form.setFieldValue(discontinuousPath, next, NO_REMOUNT);
    setChecked(next);
  }

  return (
    <FormSubsection title="Jahresmittelwerte" description="Kontinuierlich gemessene Komponenten.">
      <Stack gap="xs">
        <Grid>
          {KILN_ANNUAL_FIELDS.map((f) => {
            const valuePath = `rotaryKilns.${kilnIdx}.${f.key}Value`;
            const isHcl = f.key === 'hcl';
            const isHf = f.key === 'hf';
            const checked = isHcl ? hclChecked : isHf ? hfChecked : false;
            // KILN_ANNUAL_FIELDS ist `as const` mit heterogenen Mitgliedern -- nur HCl/HF tragen
            // die `dual`-Eigenschaft, `in` prueft das ohne einen union-weiten optionalen Typ zu
            // erzwingen (der Feldkatalog bleibt Eigentum von KilnForm.tsx, Plan 27.1-06).
            const isDual = 'dual' in f;

            return (
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={f.key}>
                <ValidatedNumberInput
                  form={form}
                  field={valuePath}
                  kind="decimal"
                  align="right"
                  label={f.label}
                  unit="mg/m³"
                  disabled={disabled || checked}
                  {...EMISSION_INPUT_PROPS}
                />
                {isDual && (
                  <Checkbox
                    label="diskontinuierlich gemessen"
                    size="xs"
                    mt={5}
                    checked={checked}
                    disabled={disabled}
                    onChange={(event) =>
                      handleToggle(
                        isHcl ? hclDiscontinuousPath : hfDiscontinuousPath,
                        valuePath,
                        isHcl ? hclStash : hfStash,
                        isHcl ? setHclStash : setHfStash,
                        isHcl ? setHclChecked : setHfChecked,
                        event.currentTarget.checked,
                      )
                    }
                  />
                )}
              </Grid.Col>
            );
          })}
        </Grid>
        <Text size="xs" c="vdzGray.6">
          HCl und HF können auch diskontinuierlich gemessen werden. Setzen Sie das Häkchen, wenn für diesen Ofen kein Jahresmittelwert vorliegt — die Werte stehen dann bei den Einzelmessungen.
        </Text>
      </Stack>
    </FormSubsection>
  );
}
